import type { FC } from 'react';

import { useMutation } from '@apollo/client';
import { useTranslation } from '@crehana/i18n';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { TextField } from '@crehana/ui';
import { get } from 'lodash';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { useForm } from 'react-hook-form';

import adjustColorBrightness from '@/shared/utils/adjustColorBrightness';
import { validations } from '@/views/Auth/constants';
import AUTH_PAGES_CHANGE_ORG_EMAIL_MUTATION from '@/views/Auth/graphql/AuthPagesChangeUserOrgEmailMutation.v5.graphql';
import type {
  AuthPagesChangeUserOrgEmailMutation,
  AuthPagesChangeUserOrgEmailMutationVariables,
  AuthPagesChangeUserOrgEmailMutation_security_change_user_organization_email_UserOrganizationValidationMutationError as ChangeEmailErrorResponse,
  AuthPagesChangeUserOrgEmailMutation_security_change_user_organization_email as ChangeEmailResponse,
  AuthPagesChangeUserOrgEmailMutation_security_change_user_organization_email_ChangeOrganizationUserEmailPayload as ChangeEmailSuccessResponse,
} from '@/views/Auth/graphql/types/AuthPagesChangeUserOrgEmailMutation';
import type { AuthPagesOrgInfoQuery_organizationInfo as OrgInfoType } from '@/views/Auth/graphql/types/AuthPagesOrgInfoQuery';
import { getEmailParam } from '@/views/Auth/utils';

import { getOrgLoginTitle } from '../utils/get-org-login-title';

const isSuccessful = (
  response: ChangeEmailResponse,
): response is ChangeEmailSuccessResponse =>
  response.__typename === 'ChangeOrganizationUserEmailPayload';

type TFormValues = {
  email: string;
};

type EmailExtraFormProps = {
  orgInfo: OrgInfoType;
  nextUrl: string;
};

const EmailExtraForm: FC<React.PropsWithChildren<EmailExtraFormProps>> = ({
  orgInfo,
  nextUrl,
}) => {
  const { t, languageCode } = useTranslation();

  // Same headline resolution as `LoginForm`: this step is still part of the org
  // login flow (it renders inside `B2BOrgLayout` right after a successful
  // login), so an org with its own copy must keep it here too.
  const formTitle =
    getOrgLoginTitle(orgInfo?.slug, languageCode) ?? t('FORM_TITLE');

  const defaultEmail = getEmailParam() as string;
  const primaryColor = orgInfo?.lookAndFeel?.primary?.main || undefined;
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<TFormValues>({
    defaultValues: {
      email: defaultEmail,
    },
  });

  const [saveEmail] = useMutation<
    AuthPagesChangeUserOrgEmailMutation,
    AuthPagesChangeUserOrgEmailMutationVariables
  >(AUTH_PAGES_CHANGE_ORG_EMAIL_MUTATION, {
    context: { clientName: 'v5' },
    onCompleted: data => {
      if (
        data?.security?.change_user_organization_email &&
        isSuccessful(data.security.change_user_organization_email)
      ) {
        window.location.replace(nextUrl);
      } else {
        setError('email', {
          type: 'manual',
          message: get(
            (
              data?.security
                ?.change_user_organization_email as ChangeEmailErrorResponse
            )?.errors,
            '[0]',
          ),
        });
      }
    },
  });

  const onSubmit = (data: TFormValues) =>
    saveEmail({
      variables: {
        input: {
          organization_id: orgInfo?.originalId?.toString() ?? '',
          email: data.email,
        },
      },
    });

  // We are using the syntax [&>div>input] to target the input element inside the wrapper
  // that's because the input doesn't have a prop to override it
  const inputWrapperClassName =
    'rounded-10 [&>div>input]:tw-font-body4 md:[&>div>input]:tw-font-body3 [&>div>input]:tw-py-0 md:[&>div>input]:tw-py-1.6 [&&&]:tw-border-neutral-light-500 md:[&&&]:border-dark-gray';

  const inputLabelClassName =
    '[&>span]:tw-font-subtitle5 md:[&>span]:tw-font-subtitle4';

  return (
    <form className="md:pt-8" onSubmit={handleSubmit(onSubmit)}>
      <div className="tw-mb-4 md:tw-mb-2.4">
        <h3 className="tw-font-h2 tw-mb-1.2">{formTitle}</h3>
        <p className="font-body3">{t('EMAIL_FORM_DESCRIPTION')}</p>
      </div>

      <TextField
        className={`tw-mb-3.2 md:tw-mb-2.4 ${inputLabelClassName}`}
        wrapperClassName={`${inputWrapperClassName}`}
        id="email"
        name="email"
        label={t('INPUT_EMAIL_LABEL')}
        hint={t('INPUT_EMAIL_HINT')}
        ref={register({
          required: validations.required(t),
          minLength: validations.minLength(t),
          maxLength: validations.maxLength(t),
          pattern: validations.email(t),
        })}
        defaultValue={defaultEmail}
        disabled={isSubmitting}
        errorText={errors.email?.message}
        full
      />

      <div className="flex flex-col">
        <button
          type="submit"
          className="tw-btn-primary-m mb-32 lg:w-200 flex justify-center"
          css={
            primaryColor &&
            `
            background-color: ${primaryColor};
            &:hover {
              background-color: ${adjustColorBrightness(primaryColor, 30)};
            }`
          }
        >
          {t(isSubmitting ? 'SENDING' : 'BUTTON_SUBMIT_EMAIL')}
        </button>
      </div>
    </form>
  );
};

export default EmailExtraForm;
